// components/VerificationChart.tsx
'use client';

import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Area,
} from 'recharts';
import { getVerificationStatistics } from '../app/api/verificationApi';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface VerificationChartProps {
  filter: string;
  dateRange: string;
  municipality?: string;
  brand?: string;
  year?: string;
}

export default function VerificationChart({ 
  filter, 
  dateRange, 
  municipality, 
  brand, 
  year 
}: VerificationChartProps) {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [range, setRange] = useState<'year' | '9m' | '6m'>('year');

  useEffect(() => {
    fetchChartData();
  }, [filter, dateRange, municipality, brand, year, range]);

  const fetchChartData = async () => {
    try {
      setLoading(true);
      const response: any = await getVerificationStatistics(filter, dateRange || undefined);
      
      if (response.success) {
        const stats = response.statistics;
        let data = [];

        if (stats.byMonth && stats.byMonth.length > 0) {
          const months = stats.byMonth;
          const sorted = months.sort((a: any, b: any) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
          });

          let filtered = sorted;
          if (range === '6m') {
            filtered = sorted.slice(-6);
          } else if (range === '9m') {
            filtered = sorted.slice(-9);
          }

          data = filtered.map((item: any) => {
            const date = new Date(item.year, item.month - 1, 1);
            return {
              month: format(date, 'MMMM yyyy', { locale: fr }),
              count: item.count || 0,
              shortMonth: format(date, 'MMM', { locale: fr }),
              year: item.year,
              monthNum: item.month,
              fullDate: date,
            };
          });
        } else if (stats.byDay && stats.byDay.length > 0) {
          const days = stats.byDay.slice(-30);
          data = days.map((item: any) => ({
            month: item.date,
            count: item.count || 0,
            shortMonth: format(parseISO(item.date), 'd MMM', { locale: fr }),
          }));
        }

        setChartData(data);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">Recensements par mois</h3>
          <p className="mt-1 text-sm text-slate-500">
            Total: <span className="font-semibold text-slate-700">{total.toLocaleString()}</span> véhicules
            {(municipality || brand || year) && (
              <span className="ml-2 text-xs text-slate-400">
                {municipality && `• ${municipality}`}
                {brand && `• ${brand}`}
                {year && `• ${year}`}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 px-2 py-1">
          <button
            onClick={() => setRange('year')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              range === 'year' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Année
          </button>
          <button
            onClick={() => setRange('9m')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              range === '9m' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            9 mois
          </button>
          <button
            onClick={() => setRange('6m')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              range === '6m' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            6 mois
          </button>
        </div>
      </div>

      <div className="mt-5 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#60A5FA" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#60A5FA" />
                <stop offset="100%" stopColor="#22C55E" />
              </linearGradient>
            </defs>
            
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            
            <XAxis
              dataKey="shortMonth"
              tick={{ fontSize: 12, fill: '#94A3B8' }}
              tickLine={false}
              axisLine={{ stroke: '#E2E8F0' }}
            />
            
            <YAxis
              tick={{ fontSize: 12, fill: '#94A3B8' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              labelStyle={{ fontWeight: 'bold', color: '#1E293B' }}
              formatter={(value: any) => [value, 'Véhicules']}
              labelFormatter={(label) => {
                const item = chartData.find(d => d.shortMonth === label);
                return item ? item.month : label;
              }}
            />
            
            <Legend />
            
            <Area
              type="monotone"
              dataKey="count"
              stroke="url(#lineGradient)"
              strokeWidth={2}
              fill="url(#areaGradient)"
              name="Véhicules"
            />
            
            <Bar
              dataKey="count"
              fill="#93C5FD"
              radius={[4, 4, 0, 0]}
              name="Véhicules"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}